import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Repeat2, Square } from 'lucide-react';
import { optionForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useTranslation } from '../../../lib/i18n';
import { useMyAgents } from '../../../lib/my-agents';
import { useDuplexer } from '../../../lib/use-duplexer';
import { taskStatusBadge } from '../../../lib/task-status';
import { AgentDot } from '../../AgentDot';
import { LinkifiedText } from '../../LinkifiedText';
import { Badge, Button, Card, ScrollArea, Typography } from '../ui';
import { useTypewriter } from '../chat-surface/loaders';
import { buildRows, entityToMessage, isTerminal, type TranscriptMessage } from '../tasks/agent-conversation-rows';
import { agentAtWorkWindow, FLIP_HALF_MS, showsCardAttribution, upsertAgentAtWorkRow, VERDICT_BADGE } from '../../../lib/agent-at-work';
import styles from './AgentAtWorkCard.module.css';

/**
 * One transcript line, revealed with the SAME typewriter the chat's own bubbles use, so a turn arriving
 * here reads like a turn arriving there rather than snapping in whole.
 *
 * `animate` is captured ONCE and never re-read. Flipping `useTypewriter`'s `skipInitial` mid-reveal
 * re-runs its effect, which early-returns and clears the interval — freezing the text truncated for good
 * (the trap written up against `MessageRow` in this package's CLAUDE.md). So a row that stops being the
 * newest keeps typing to completion instead of locking mid-word.
 */
function CardTurnText({ text, animate }: { text: string; animate: boolean }) {
  const [types] = useState(animate);
  return <LinkifiedText text={useTypewriter(text, !types)} />;
}

// The in-chat "agent at work" moment — the live counterpart to `AgentJoinCard`. Rendered in place of a
// text bubble for an ASSISTANT message carrying `metadata.agentAtWorkCard`, which the Maestro emits when
// it spawns an agent onto a task.
//
// Where the join card is a SNAPSHOT of something that happened, this is a WINDOW on work still in motion.
// **The chat row it rides carries only `taskId`** — everything on screen is read live off that task (a
// history row cannot be updated as the work proceeds without invalidating every cached token behind it),
// via `/api/task/:id/at-work`: the working agent's own avatar with an activity ring turning around it, the
// task's current status, the sentence it wrote about what it is doing, and the newest turns of its own
// conversation streaming in.
//
// A task has TWO sides — the agent doing the work and the agent reviewing it — and the card shows one at a
// time, flipping when you click the identity block. The flip is a real card flip: it turns edge-on around
// its vertical axis, the content swaps at the halfway point, and it turns back with the other side facing
// you (two halves rather than one 0→180 sweep, which would render every word mirrored for the whole second
// half). Both sides are already loaded, so a flip is instant, and the control appears only once a review
// pass exists.
//
// The live pieces come from two sources, split by what can push: the ROWS arrive over the chat duplexer
// channel, while the title, status and action sentence have no push and ride a slow poll.
// That is the same division the task screen's `AgentConversationPanel` makes, and the fold and the row
// shapes are literally the same code (`agent-conversation-rows.ts`), so a beat cannot read one way in the
// card and another on the task screen.
//
// Stop halts the run AND tells the MAESTRO its task was called off — one server action
// (`/api/tasks/:id/cancel` → native-data `task-cancel`), the same one the task screen's Cancel uses. It
// requests the per-conversation stop flag, and writes a SYSTEM row into the Maestro's chat.
//
// What Stop guarantees is "nothing further HAPPENS", not that the run dies on the click. The sub-agent's
// completion arrives as a separate event (there is no local stream to abort, and nothing in this codebase
// cancels a prediction at the gateway), so the model call in flight finishes — but the loop then reads the
// task's own status before running a single tool and ends there. So no tool call, no file, no message after
// the press; only a tool already mid-execution finishes, because tools take no AbortSignal.
//
// The task's STATUS is the signal, deliberately, not the per-conversation Stop flag: `BaseKVStore` is
// scoped per ARTIFACT, so a flag the serverless server sets is invisible to the harness loop. That was a
// real bug — a stopped run wrote a file two minutes later. Stop shows only while the task is unfinished.
//
// Shared 1:1 with the native `AgentAtWorkCard`s.

// The duplexer streams new rows; this slow poll refreshes the title, status and action sentence (nothing
// pushes those), notices a reviewer that starts mid-run, and reconciles a dropped socket.
// 15s, matching `AgentConversationPanel`'s own fallback so the two never disagree about freshness.
const POLL_MS = 15000;

export interface AgentAtWorkCardMarker {
  taskId: string;
  agentId?: string;
  agentName?: string;
  agentTitle?: string;
  taskTitle?: string;
}

interface AtWorkResponse {
  found: boolean;
  workerConversationId?: string;
  reviewerConversationId?: string;
  reviewerAgentId?: string;
  /** Where the review stands — the reviewer's verdict and its own words. null before the first submission. */
  reviewVerdict?: { stage: 'submitted' | 'approved' | 'changesRequested' | 'failed'; content: string } | null;
  workerRows?: TranscriptMessage[];
  reviewerRows?: TranscriptMessage[];
  title?: string;
  status?: string;
  currentAction?: string;
  isLive?: boolean;
}

type Side = 'worker' | 'reviewer';

export function AgentAtWorkCard({ card }: { card: AgentAtWorkCardMarker }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const queryClient = useQueryClient();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const rosterName = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.name || undefined : undefined);
  const rosterTitle = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.title || undefined : undefined);

  const [side, setSide] = useState<Side>('worker');
  const [flipPhase, setFlipPhase] = useState<'' | 'out' | 'back'>('');
  const transcriptRef = useRef<HTMLElement>(null);

  const queryKey = ['task-at-work', card.taskId];
  const { data, isError, isLoading } = useQuery({
    queryKey,
    enabled: !!card.taskId,
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(card.taskId)}/at-work`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as AtWorkResponse;
    },
    // Stops the moment the task is terminal — no more rows will come.
    refetchInterval: (d) => (isTerminal((d as AtWorkResponse | undefined)?.status) ? false : POLL_MS),
  });

  // The card's own async region reports the 198:4 / 198:5 pair with real timing and outcome.
  useLoadBi('agent-at-work-card', { isLoading, isError });

  const workerConversationId = data?.workerConversationId ?? '';
  const reviewerConversationId = data?.reviewerConversationId ?? '';
  // Keyed on the reviewer's IDENTITY, not its conversation: the reviewer is chosen at SPAWN, so it is
  // known from the first frame — long before it has a conversation or anything to say. Keyed on the
  // conversation, the flip would be hidden for exactly the period when "who will check this?" is live.
  const hasReviewer = !!data?.reviewerAgentId;

  // Stream new rows live, for BOTH sides, into the same cache the poll fills. One subscription serves the
  // whole card: the row's own conversationId decides which side it belongs to, which is what makes a flip
  // instant rather than a fresh load.
  useDuplexer(
    useCallback(
      (entity: { conversationId?: string }) => {
        const target: Side | null =
          entity.conversationId && entity.conversationId === workerConversationId
            ? 'worker'
            : entity.conversationId && entity.conversationId === reviewerConversationId
            ? 'reviewer'
            : null;
        if (!target) return;
        const msg = entityToMessage(entity as Parameters<typeof entityToMessage>[0]);
        if (!msg) return;
        queryClient.setQueryData<AtWorkResponse>(queryKey, (prev) => {
          if (!prev) return prev;
          const key = target === 'worker' ? 'workerRows' : 'reviewerRows';
          return { ...prev, [key]: upsertAgentAtWorkRow(prev[key] ?? [], msg) };
        });
      },
      [workerConversationId, reviewerConversationId, queryClient, card.taskId],
    ),
  );

  const stop = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(card.taskId)}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    // Re-read straight away rather than waiting out the poll: the user just acted, and a card that keeps
    // saying "In progress" afterwards reads as a button that did nothing. The status is deliberately NOT
    // written optimistically — this card's job is to show what is actually happening, and flipping it to
    // "Cancelled" before the agent has been halted would state that the work stopped when we only asked.
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const terminal = isTerminal(data?.status);
  const live = !!data?.isLive && !terminal;

  // A review pass that ends must not leave the card showing a side that no longer exists.
  const shownSide: Side = hasReviewer ? side : 'worker';
  const sideAgentId = shownSide === 'worker' ? card.agentId : data?.reviewerAgentId || undefined;

  // The worker falls back to the row's first-frame hint until the roster resolves; the reviewer has no
  // hint on the row (it may not have existed at spawn), so it falls back to the localized role word.
  const sideName =
    shownSide === 'worker'
      ? rosterName(card.agentId) ?? card.agentName ?? t('chat.agentAtWork.worker')
      : rosterName(data?.reviewerAgentId) ?? t('chat.agentAtWork.reviewer');
  // The reviewing agent's own job title says what it does in general, not that it is reviewing HERE,
  // which is the thing worth naming after a flip.
  const sideRole = shownSide === 'worker' ? rosterTitle(card.agentId) ?? card.agentTitle : t('chat.agentAtWork.reviewerRole');
  // `t()` is typed as possibly-undefined, and the colour key must be a real string — the option
  // table is deterministic on whatever it is given, so an empty key is valid rather than a crash.
  const sideColorKey = sideAgentId ?? sideName ?? '';
  const sideAccent = optionForAgent(sideColorKey).accent;

  const verdict = data?.reviewVerdict ?? undefined;
  // The WORKER's name, for the pre-review sentence while the reviewer side is showing.
  const workerName = rosterName(card.agentId) ?? card.agentName ?? t('chat.agentAtWork.worker');
  const taskTitle = data?.title || card.taskTitle || '';
  const badge = taskStatusBadge(data?.status ?? 'IN_PROGRESS');
  // The same ladder every other working surface uses: the model's own `currentAction`, else nothing. A
  // card whose first read has not landed says it is starting rather than showing an empty gap.
  const actionSentence = data?.currentAction?.trim() || (terminal ? '' : t('chat.agentAtWork.starting'));

  const rows = buildRows((shownSide === 'worker' ? data?.workerRows : data?.reviewerRows) ?? []);
  const visibleRows = agentAtWorkWindow(rows);

  // Stick the capped pane to its newest line while the work is live, and only then: once a run is over
  // the reader owns the scroll, and yanking them back to the bottom of a finished transcript they were
  // reading up through is the behaviour every chat gets wrong. Keyed on the rendered text as well as the
  // row count so a row GROWING (the typewriter, a streamed turn) keeps the newest words in view rather
  // than only a whole new row doing so.
  const newestText = visibleRows[visibleRows.length - 1]?.text ?? '';
  useEffect(() => {
    const pane = transcriptRef.current;
    if (!pane || !live) return;
    pane.scrollTop = pane.scrollHeight;
  }, [live, shownSide, visibleRows.length, newestText]);

  const flip = () => {
    if (!hasReviewer || flipPhase) return;
    logPageClickBi({
      page_name: 'chat',
      button_name: 'agent_at_work_flip',
      // Which way it went, so the funnel can tell "opened the reviewer" from "went back".
      answer: side === 'worker' ? 'reviewer' : 'worker',
    });
    // Two halves, swapping at edge-on — a single sweep would render every word mirrored for the whole
    // second half. Honours reduced motion by swapping with no rotation.
    if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setSide((s) => (s === 'worker' ? 'reviewer' : 'worker'));
      return;
    }
    setFlipPhase('out');
    setTimeout(() => {
      setSide((s) => (s === 'worker' ? 'reviewer' : 'worker'));
      setFlipPhase('back');
      requestAnimationFrame(() => setFlipPhase(''));
    }, FLIP_HALF_MS);
  };

  const showStop = !terminal && data?.found !== false;

  return (
    <Card
      variant="sunken"
      className={[styles.card, hasReviewer && styles.clickable, flipPhase === 'out' && styles.flipOut, flipPhase === 'back' && styles.flipBack]
        .filter(Boolean)
        .join(' ')}
      // The WHOLE card flips, not just the identity block. A three-line name/title/role region was the
      // only hit target, which read as an accident: nothing about a card whose every part describes one
      // agent's work suggests that only the top-left third of it turns over. Deliberately NOT the Card's
      // `component="button"` either — Stop lives inside it, and nesting one button element in another is
      // invalid HTML, so this is a role="button" region and Stop stops the click from reaching it.
      {...(hasReviewer
        ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-label': t('chat.agentAtWork.flipHint'),
            onClick: flip,
            onKeyDown: (e: ReactKeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                flip();
              }
            },
          }
        : {})}
      data-testid="agent-at-work-card"
    >
      <div className={styles.head}>
        {/* The working agent's OWN avatar, with a dashed activity ring turning around it.
            The agent is the centre and the RING is what moves: the motion is the work, not the identity.
            Deliberately not the join card's Maestro-at-the-centre motif — that card is about the TEAM. */}
        <span className={styles.mark}>
          <span className={styles.ring} style={{ borderColor: sideAccent }} aria-hidden />
          {/* A dot travelling the ring is what actually reads as motion. Rotating the dashed ring alone
              was invisible: a uniform dash pattern turning looks identical to a still one, so the card
              claimed to be working and appeared frozen. The ring stays put and the dot orbits it. */}
          {live && (
            <span className={styles.orbit} aria-hidden>
              <span className={styles.orbitDot} style={{ background: sideAccent }} />
            </span>
          )}
          <AgentDot id={sideColorKey} size={40} />
        </span>

        {/* Plain text now: the card itself is the trigger, so this carries no button chrome and no hover
            fill of its own — the grey box it used to grow on hover made the region look like a misplaced
            input rather than the card's own heading. */}
        <div className={styles.identity} data-testid="agent-at-work-identity">
          {/* The AGENT leads: this is the agent's card, not the task's — the point of it is that somebody
              is doing this work, and the flip goes between two AGENTS. The task gets its own line
              directly under the name, in primary ink at the heading role one step down, so it reads as
              the subtitle it is rather than as metadata in caption grey. */}
          <span className={styles.nameRow}>
            <Typography variant="h3">{sideName}</Typography>
            {hasReviewer && <Repeat2 size={14} aria-hidden className={styles.flipIcon} />}
          </span>
          <Typography variant="h4" className={styles.taskTitle}>
            {taskTitle}
          </Typography>
          {!!sideRole && (
            <span className={styles.rolePill}>
              <Typography variant="p3" color="secondary">
                {sideRole}
              </Typography>
            </span>
          )}
        </div>

        <Badge variant={badge.variant} label={t(badge.labelKey)} />
      </div>

      {/* Worker side only: `currentAction` is the task's one action sentence and the worker is who writes
          it, so repeating it under the reviewer would put the worker's words in the reviewer's mouth. */}
      {shownSide === 'worker' && !!actionSentence && (
        <div className={styles.action} data-testid="agent-at-work-action">
          {live && <span className={styles.pulse} aria-hidden />}
          <Typography variant="p2">{actionSentence}</Typography>
        </div>
      )}

      {/* What the REVIEWER side says above its transcript — the point of flipping there. Three states,
          and the pre-review one is why the flip is offered from the start: the reviewer is assigned at
          spawn, so for the whole of the work "who checks this, and when?" has a real answer that nothing
          else on the card gives. An empty panel there answered it with nothing. */}
      {shownSide === 'reviewer' && (
        <div className={styles.review} data-testid="agent-at-work-review">
          {verdict ? (
            <>
              {/* Every review state wears the badge, so "In review" reads in the same language as the
                  verdicts either side of it rather than as a bare sentence. */}
              <Badge variant={VERDICT_BADGE[verdict.stage].variant} label={t(VERDICT_BADGE[verdict.stage].labelKey)} />
              {verdict.stage === 'submitted' ? (
                // The entry's content here is the WORKER's note to the reviewer, NOT a verdict — rendering
                // it under this badge would attribute the worker's words to the reviewer.
                <Typography variant="p2" color="secondary">
                  {t('chat.agentAtWork.awaitingReview', { reviewer: sideName })}
                </Typography>
              ) : (
                // The reviewer's OWN words — agent text, so it goes through LinkifiedText like any turn.
                !!verdict.content && <LinkifiedText text={verdict.content} />
              )}
            </>
          ) : (
            // Pre-review: name BOTH sides, because the sentence is about the handover.
            <Typography variant="p2" color="secondary">
              {t('chat.agentAtWork.preReview', { reviewer: sideName, agent: workerName })}
            </Typography>
          )}
        </div>
      )}

      {isError || data?.found === false ? (
        // An explicit failure, never an empty transcript standing in for one.
        <Typography variant="p3" color="secondary" data-testid="agent-at-work-error">
          {t('tasks.detail.conversationError')}
        </Typography>
      ) : visibleRows.length === 0 ? (
        // The WORKER side says so plainly. The reviewer side is silent: its block above already said where
        // the review stands, and following that with "nothing on this side yet" says the absence twice.
        shownSide === 'worker' ? (
          <Typography variant="p3" color="secondary" data-testid="agent-at-work-empty">
            {t('chat.agentAtWork.noTurnsYet')}
          </Typography>
        ) : null
      ) : (
        /* A CAPPED, scrolling pane — the row window alone does not bound the card. `AGENT_AT_WORK_WINDOW`
           limits how many TURNS are kept, and a turn is not a line: one long reasoning turn filled a whole
           phone screen and pushed the composer and the rest of the conversation out of view. So the height
           is capped and the work scrolls inside it, stuck to the newest line while a run is live so it
           reads as a live pane rather than one the reader has to chase. */
        <ScrollArea ref={transcriptRef} className={styles.transcript} data-testid="agent-at-work-transcript">
          {visibleRows.map((row, i) => {
            // Almost always NO attribution header — the card has already named the agent one element
            // above, and each side shows only that side's own conversation. Stamping the name on every
            // turn repeated it three or four times inside a four-row window. The one case that earns a
            // header is a turn by someone OTHER than the side named at the top: a foreign voice, which is
            // exactly what a reader cannot infer from the header. Timestamps come off for the same
            // reason — the task screen is the record of the work and wants them; the card is a window on
            // what is happening now.
            const showsAgent = showsCardAttribution(visibleRows, i, sideAgentId);
            const name = rosterName(row.agentId) ?? (row.agentId === card.agentId ? card.agentName : undefined);
            if (row.kind === 'act') {
              return (
                <div key={`act-${i}-${row.text}`} className={styles.act}>
                  <span className={styles.beat} aria-hidden />
                  <Typography variant="p3" color="secondary">
                    {row.text}
                  </Typography>
                </div>
              );
            }
            return (
              <div key={`turn-${i}-${row.text.slice(0, 24)}`} className={styles.turn}>
                {showsAgent && (
                  <span className={styles.attr}>
                    {!!row.agentId && <AgentDot id={row.agentId} size={16} />}
                    {!!name && <Typography variant="h4">{name}</Typography>}
                  </span>
                )}
                {/* Quiet by design — the whole transcript is small and secondary-ink, the register a
                    reasoning trace reads in, not the register of the agent's ANSWER. What the agent
                    finally says arrives in the chat as its own message; this is a window on it thinking,
                    so it must never compete with the surrounding conversation. */}
                <Typography variant="p3" component="span" color="secondary">
                  <CardTurnText text={row.text} animate={live && i === visibleRows.length - 1} />
                </Typography>
              </div>
            );
          })}
        </ScrollArea>
      )}

      {showStop && (
        <div className={styles.foot}>
          {
            // Deliberately quiet — the DS outlined skin rather than a filled destructive one: stopping
            // your own agent is an ordinary steering decision, not a dangerous act, and it sits inside a
            // card the reader is otherwise just reading.
            <Button
              variant="outlined"
              size="tiny"
              loading={stop.isLoading}
              startIcon={<Square size={12} aria-hidden />}
              onClick={(e) => {
                // The card is the flip trigger, so a Stop press must not also turn the card over.
                e.stopPropagation();
                logPageClickBi({ page_name: 'chat', button_name: 'agent_at_work_stop' });
                stop.mutate();
              }}
              data-testid="agent-at-work-stop"
            >
              {t(stop.isLoading ? 'chat.agentAtWork.stopping' : 'chat.agentAtWork.stop')}
            </Button>
          }
        </div>
      )}
    </Card>
  );
}
